'use client';

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  FC,
  useEffect,
} from 'react';

interface ScrollRestoreContextType {
  gridScroll: number;
  setGridScroll: React.Dispatch<React.SetStateAction<number>>;
  visibleCount: number;
  setVisibleCount: React.Dispatch<React.SetStateAction<number>>;
  lastVisitedUid: string | null;
  setLastVisitedUid: React.Dispatch<React.SetStateAction<string | null>>;
  timestamp: number | null;
  setTimestamp: React.Dispatch<React.SetStateAction<number | null>>;
  isExpired: boolean;
}

export const GridScrollRestoreContext = createContext<
  ScrollRestoreContextType | undefined
>(undefined);

interface GridScrollRestoreProviderProps {
  children: ReactNode;
}

export const GridScrollRestoreProvider: FC<GridScrollRestoreProviderProps> = ({
  children,
}) => {
  const [gridScroll, setGridScroll] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [lastVisitedUid, setLastVisitedUid] = useState<string | null>(null);
  const [timestamp, setTimestamp] = useState<number | null>(null);

  const isExpired = timestamp ? Date.now() - timestamp > 5 * 60 * 1000 : false;

  useEffect(() => {
    if (isExpired) {
      setGridScroll(0);
      setLastVisitedUid(null);
      setTimestamp(null);
    }
  }, [isExpired]);

  return (
    <GridScrollRestoreContext.Provider
      value={{
        gridScroll,
        setGridScroll,
        visibleCount,
        setVisibleCount,
        lastVisitedUid,
        setLastVisitedUid,
        timestamp,
        setTimestamp,
        isExpired,
      }}
    >
      {children}
    </GridScrollRestoreContext.Provider>
  );
};

export const useGridScrollRestore = () => {
  const context = useContext(GridScrollRestoreContext);

  if (context === undefined) {
    throw new Error(
      'useGridScrollRestore must be used within a GridScrollRestoreProvider',
    );
  }
  return context;
};
